import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Grid,
  makeStyles,
  IconButton,
  List,
  Tooltip,
} from "@material-ui/core";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import CloseIcon from "@material-ui/icons/Close";

import { Wrapper, TabItem } from "./styles";
import { tableSelector, getTable, tableSuccess, handlerChangeTable } from "../../../redux/tableReducer";
import { getTableReports } from "../../../redux/reportReducer";
import { authSelector } from "../../../redux/authReducer";

import Preloader from "../../Common/Preloader";
import JackpotsList from "../../Jackpots/List";
import TableReports from "./Reports";
import TableForm from "../Form";
import GoBackBtn from "../../Common/GoBackBtn";
import Denominations from "./denominations";
import Api from "./api";
import WagersView from "./wagers";
import GamePackages from "../../Halls/View/game-packages";

import { findPermission, findPermissionOr } from "../../../utils/find";
import { TAB_API, TAB_DENOMINATION, TAB_JACKPOTS, TAB_MAIN, TAB_PACKETS, TAB_REPORTS, TAB_WAGER } from "../tabs";

import editPng from "../../../assets/images/edit.png";
import visionPng from "../../../assets/images/vision.png";
import MainTabInfo from "./main-tab-info";

const useStyles = makeStyles(() => ({
  closeBtn: {
    position: "absolute",
    top: "0",
    right: "0",
  },
}));

interface TableProps {
  hideTable: () => void;
  showTableSessions: () => void;
  showOperations: () => void;
  tableId: number;
  isChangeMode?: boolean;
  setIsChangeMode: (isChangeMode: boolean) => void;
  showPayment: () => void;
}

const Table: React.FC<TableProps> = ({
  tableId,
  isChangeMode = false,
  setIsChangeMode,
  showPayment
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ prevPaths?: string[] }>();
  const [t] = useTranslation();

  const { data: table, loading } = useSelector(tableSelector);
  const { role: { item: userRole }, assigments: { items } } = useSelector(authSelector);

  const [tab, setTab] = useState(TAB_MAIN);
  const [isChange, setIsChange] = useState(isChangeMode);

  const canShowApi = useMemo(() => findPermission(items, "showApi"), [items]);
  const isNotReferral = useMemo(() => userRole && userRole !== "referral", [userRole]);
  const canViewGamePackages = useMemo(() => findPermission(items, "viewGamePackageList"), [items]);

  useEffect(() => {
    if (tableId) dispatch(getTable(tableId));
    return () => { dispatch(tableSuccess(null)); };
  }, [dispatch, tableId]);

  useEffect(() => {
    return () => { setIsChangeMode(false); };
  }, [setIsChangeMode]);

  const returnTab = (tab_id: number) => {
    if (!table) return null;

    switch (tab_id) {
      case TAB_MAIN:
        return <MainTabInfo table={table} showPayment={showPayment} isNotReferral={isNotReferral || false} />;
      case TAB_PACKETS:
        return <GamePackages packages={table.packages} />;
      case TAB_JACKPOTS:
        return <JackpotsList change table_id={tableId} />;
      case TAB_REPORTS:
        return (
          <TableReports
            tableId={tableId}
            fetch={(id: number, from: string, to: string) => dispatch(getTableReports(id, from, to))}
            currency_id={table.hall?.currency_id}
          />
        );
      case TAB_DENOMINATION:
        return <Denominations table={table} />;
      case TAB_API:
        return <Api table={table} />;
      case TAB_WAGER:
        return <WagersView table={table} />;
      default:
        return null;
    }
  };

  const renderHeaderButtons = () => (
    <Box className={classes.closeBtn}>
      {findPermission(items, "viewGamblingTable") &&
        findPermissionOr(items, ["updateGamblingTable", "updateGamblingTableChildren"]) && (
          <Tooltip title={String(isChange ? t("ru.common.view_tooltip") : t("ru.common.change_btn"))} placement="top">
            <IconButton onClick={() => setIsChange(!isChange)}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{
                  background: "#b6b6b6b6",
                  width: "30px",
                  minHeight: "30px",
                  borderRadius: "50%",
                  padding: "6px",
                }}
              >
                <img style={{ width: "20px", minHeight: "auto" }} src={!isChange ? editPng : visionPng} alt="icon" />
              </Box>
            </IconButton>
          </Tooltip>
        )}
      <IconButton onClick={() => history.push("tables")}>
        <CloseIcon />
      </IconButton>
    </Box>
  );

  const rootRender = !table || loading ? (
    <Preloader absolute={false} />
  ) : (
    <React.Fragment>
      {location.state?.prevPaths && !!location.state.prevPaths.length && <GoBackBtn />}

      {renderHeaderButtons()}

      <Box display="flex" alignItems="center">
        <AccountCircleIcon fontSize="large" />
        <h2>{table.name}</h2>
      </Box>

      {isChange ? (
        <TableForm
          onSubmit={(data, packages, hideForm, id, info) =>
            id && dispatch(handlerChangeTable(data, packages, hideForm, id, info))
          }
          id={table.id}
          name={table.name}
          hall_id={table.hall_id}
          packets={table.packages}
          status={table.status}
          cancelBtnText={t("ru.common.back_btn")}
          cancelBtnEvent={() => setIsChange(false)}
          submitBtnText={t("ru.common.submit_btn")}
          hideForm={() => setIsChange(false)}
          tab={tab}
          setTab={setTab}
          denomination={table.denomination}
          denominationList={table.denominationList}
          api={table.api ?? undefined}
          phone={table.phone}
          email={table.email}
        />
      ) : (
        <React.Fragment>
          <Box className="tabs" display="flex" justifyContent="flex-start" style={{ overflowX: "auto" }}>
            <TabItem active={tab === TAB_MAIN} onClick={() => setTab(TAB_MAIN)}>
              {t("ru.tables.tabs.info")}
            </TabItem>
            <TabItem active={tab === TAB_DENOMINATION} onClick={() => setTab(TAB_DENOMINATION)}>
              {t("ru.tables.tabs.denomination")}
            </TabItem>
            {canViewGamePackages && (
              <TabItem active={tab === TAB_PACKETS} onClick={() => setTab(TAB_PACKETS)}>
                {t("ru.tables.tabs.packets")}
              </TabItem>
            )}
            {userRole && userRole !== "cashier" && (
              <TabItem active={tab === TAB_JACKPOTS} onClick={() => setTab(TAB_JACKPOTS)}>
                {t("ru.tables.tabs.jackpots")}
              </TabItem>
            )}
            {userRole && userRole !== "cashier" && (
              <TabItem active={tab === TAB_REPORTS} onClick={() => setTab(TAB_REPORTS)}>
                {t("ru.tables.tabs.reports")}
              </TabItem>
            )}
            {canShowApi && (
              <TabItem active={tab === TAB_API} onClick={() => setTab(TAB_API)}>
                {t("ru.tables.tabs.api")}
              </TabItem>
            )}
            {userRole && userRole !== "referral" && (
              <TabItem active={tab === TAB_WAGER} onClick={() => setTab(TAB_WAGER)}>
                {t("ru.tables.tabs.wager")}
              </TabItem>
            )}
          </Box>
          <List>
            <Grid container spacing={2}>
              {returnTab(tab)}
            </Grid>
          </List>
        </React.Fragment>
      )}
    </React.Fragment>
  );

  return <Wrapper>{rootRender}</Wrapper>;
};

export default Table;